import React, { useEffect, useRef } from 'react'
import { Stack } from 'react-bootstrap'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import isEmpty from 'lodash/isEmpty'
import type { DbRestoreStatus } from '@ztechapps/common.contract'
import { DbRestoreStageStatus, DbRestoreStageStatusEnum } from '@ztechapps/common.contract'
import { useDbRestoreOutput } from '@ztechapps/common.hook'
import { formatNumber, toDate } from '@ztechapps/common.utils'
import { BusyButton } from '@ztechapps/webui.button'
import { Modal } from '@ztechapps/webui.modal'
import { Alert, HorizontalStack, Scrollable, ShowIf, Spinner, StatusColorDot, findStatus } from '@ztechapps/webui.util'

const LOG_MAX_HEIGHT = '12rem'
// Taller than the log box: a failed run's error holds the exit message and a 20-line output tail.
const ERROR_MAX_HEIGHT = '24rem'

export type DbRestoreModalProps = {
  status?: DbRestoreStatus
  failed: boolean
  interrupted: boolean
  onExit: () => void
}

/**
 * Blocks the app while the database restores — static backdrop, no close button, no drag handle.
 * Subscribes to the live restore output from mount and shows it under the stage list while the restore runs.
 */
export const DbRestoreModal = ({ status, failed, interrupted, onExit }: DbRestoreModalProps) => {
  const { lines, unavailable } = useDbRestoreOutput()
  const logRef = useRef<HTMLDivElement>(null)
  const stages = status?.stages ?? []
  const firstOpen = findIndex(stages, (stage) => stage.status !== DbRestoreStageStatusEnum.Done)
  const step = firstOpen === -1 ? stages.length : firstOpen + 1
  const failedStage = find(stages, (stage) => stage.status === DbRestoreStageStatusEnum.Failed)
  const title = interrupted ? 'Restore interrupted' : failed ? 'Restore failed' : 'Restoring database'
  // A failed restore shows only its error, so the Log out button stays in view.
  const showLog = !failed && !interrupted && (!isEmpty(lines) || unavailable)

  // Scrolls the log box itself, not the page: scrollIntoView would also move the modal.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [lines, unavailable])

  return (
    <Modal show size="xl" backdrop="static" keyboard={false} trigger={() => null}>
      <Modal.Header>
        <h4 className="m-0 text-body">{title}</h4>
      </Modal.Header>
      <Modal.Body>
        <Stack gap={3}>
          <ShowIf condition={!failed && !interrupted}>
            <Stack gap={1}>
              <span>The app is unavailable while the database is restored.</span>
              <small className="text-secondary">
                This usually takes 6–7 minutes. You will be signed out when it finishes.
              </small>
            </Stack>
          </ShowIf>
          <ShowIf condition={failed}>
            <Alert type="danger" className="mb-0">
              {failedStage?.label} failed:
              <Scrollable height={ERROR_MAX_HEIGHT} showThumb={false} className="mt-2">
                <pre className="mb-0 font-monospace small">{status?.error}</pre>
              </Scrollable>
            </Alert>
          </ShowIf>
          <ShowIf condition={interrupted}>
            <Alert
              type="warning"
              className="mb-0"
              text="The API restarted before the restore finished, so the database may be incomplete. Log out, then run Restore Db again."
            />
          </ShowIf>
          <ShowIf condition={!interrupted}>
            <span className="fw-semibold">
              Step {step} of {stages.length}
            </span>
            <Stack gap={2}>
              {stages.map((stage) => (
                <HorizontalStack key={stage.key} gap={2}>
                  <StatusColorDot value={stage.status} color={findStatus(DbRestoreStageStatus, stage.status)} />
                  <span className={stage.status === DbRestoreStageStatusEnum.Pending ? 'text-secondary' : 'text-body'}>
                    {stage.label}
                  </span>
                  <ShowIf condition={stage.status === DbRestoreStageStatusEnum.Running}>
                    <Spinner animation="border" size="sm" />
                  </ShowIf>
                  <ShowIf condition={!!stage.startedAt}>
                    <small className="ms-auto text-secondary">
                      {formatNumber(
                        toDate(stage.finishedAt ?? status.updatedAt).diff(toDate(stage.startedAt), 'seconds'),
                        '00:00:00'
                      )}
                    </small>
                  </ShowIf>
                </HorizontalStack>
              ))}
            </Stack>
          </ShowIf>
          <ShowIf condition={showLog}>
            <Scrollable
              ref={logRef}
              height={LOG_MAX_HEIGHT}
              showThumb={false}
              className="border rounded bg-body-tertiary p-2"
            >
              <div className="font-monospace small text-break">
                {lines.map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
                <ShowIf condition={unavailable}>
                  <div className="text-secondary">Live output is unavailable.</div>
                </ShowIf>
              </div>
            </Scrollable>
          </ShowIf>
        </Stack>
      </Modal.Body>
      <ShowIf condition={failed || interrupted}>
        <Modal.Footer>
          <BusyButton text="Log out" variant="primary" onClick={onExit} />
        </Modal.Footer>
      </ShowIf>
    </Modal>
  )
}
